import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthProvider';
import { useI18n } from '@/i18n/LanguageContext';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface ChangeNotification {
  id: string;
  car_id: string;
  changed_by: string;
  changes: Record<string, { old: any; new: any }>;
  changer_display_name: string | null;
  car_make: string | null;
  car_model: string | null;
  car_license_plate: string | null;
}

// Field name mapping for display
const fieldDisplayNames: Record<string, string> = {
  make: 'Make',
  model: 'Model',
  year: 'Year',
  license_plate: 'License Plate',
  vin: 'VIN',
  mileage: 'Mileage',
  transmission: 'Transmission',
  fuel_type: 'Fuel Type',
  ownership_type: 'Ownership Type',
  insurance_provider: 'Insurance Provider',
  insurance_expiry: 'Insurance Expiry',
  last_service_date: 'Last Service Date',
  image_url: 'Image URL',
  image_path: 'Image Path',
  winter_tires_brand: 'Winter Tires Brand',
  winter_tires_size: 'Winter Tires Size',
  summer_tires_brand: 'Summer Tires Brand',
  summer_tires_size: 'Summer Tires Size',
  tire_pressure: 'Tire Pressure',
  wiper_type: 'Wiper Type',
  wiper_front_size: 'Wiper Front Size',
  wiper_rear_size: 'Wiper Rear Size',
  wiper_last_replacement: 'Wiper Last Replacement',
  wiper_drivers_side_size: 'Wiper Driver Side Size',
  wiper_passanger_side_size: 'Wiper Passenger Side Size',
  octa_policy_number: 'OCTA Policy Number',
  octa_expiry: 'OCTA Expiry',
  octa_provider: 'OCTA Provider',
  octa_claims_hotline: 'OCTA Claims Hotline',
  octa_media_paths: 'OCTA Documents',
  kasko_policy_number: 'KASKO Policy Number',
  kasko_expiry: 'KASKO Expiry',
  kasko_provider: 'KASKO Provider',
  kasko_claims_hotline: 'KASKO Claims Hotline',
  kasko_media_paths: 'KASKO Documents',
};

const CarChangeNotification: React.FC = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [notification, setNotification] = useState<ChangeNotification | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const checkForNotifications = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch undismissed change notifications
        const { data: notifications, error } = await supabase
          .from('car_change_notifications')
          .select('id, car_id, changed_by, changes')
          .eq('user_id', user.id)
          .is('dismissed_at', null)
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error fetching change notifications:', error);
          setLoading(false);
          return;
        }

        if (notifications && notifications.length > 0) {
          const notif = notifications[0];
          
          // Fetch car details
          const { data: carData } = await supabase
            .from('cars')
            .select('make, model, license_plate')
            .eq('id', notif.car_id)
            .single();
          
          // Fetch changer's display name using Edge Function
          let changerDisplayName: string | null = null;
          try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
            const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
            const { data: { session } } = await supabase.auth.getSession();
            
            if (session) {
              const response = await fetch(
                `${supabaseUrl}/functions/v1/get-user-display-names`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                    'apikey': supabaseAnonKey,
                  },
                  body: JSON.stringify({ userIds: [notif.changed_by] }),
                }
              );

              if (response.ok) {
                const data = await response.json();
                if (data.success && data.displayNames && data.displayNames[notif.changed_by]) {
                  changerDisplayName = data.displayNames[notif.changed_by];
                }
              }
            }
          } catch (error) {
            console.error('Error fetching changer display name:', error);
          }
          
          setNotification({
            ...notif,
            changer_display_name: changerDisplayName,
            car_make: carData?.make || null,
            car_model: carData?.model || null,
            car_license_plate: carData?.license_plate || null,
          });
        }
      } catch (error) {
        console.error('Error checking for change notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    checkForNotifications();
    
    // Also check periodically (every 30 seconds) in case user is on the app when changes are made
    const interval = setInterval(checkForNotifications, 30000);
    
    // Check when window gains focus (user returns to app)
    const handleFocus = () => {
      checkForNotifications();
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.id]);

  const handleResponse = async (action: 'accept' | 'revert') => {
    if (!notification || !user?.id || processing) return;

    setProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-car-change-response`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            notificationId: notification.id,
            action,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to process response');
      }

      // Refresh cars list if revert (to show reverted values)
      if (action === 'revert') {
        // Reload page to show reverted values
        window.location.reload();
        return;
      }
      
      // Dismiss the notification
      setNotification(null);
    } catch (error) {
      console.error('Error handling change response:', error);
      // Show error toast or message
      alert(error instanceof Error ? error.message : 'Failed to process response');
    } finally {
      setProcessing(false);
    }
  };

  if (loading || !notification) {
    return null;
  }

  const changerName = notification.changer_display_name || 'Someone';
  const carInfo = [
    notification.car_make,
    notification.car_model,
    notification.car_license_plate,
  ]
    .filter(Boolean)
    .join(' ') || 'this car';

  const changes = notification.changes as Record<string, { old: any; new: any }>;
  const changeEntries = Object.entries(changes);

  return (
    <Dialog open={!!notification} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md" 
        style={{ fontFamily: 'Lato' }}
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-black" style={{ fontFamily: 'Lato', fontWeight: 700 }}>
            {t('carSharing.carChanges') || 'Car Details Changed'}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-2" style={{ fontFamily: 'Lato' }}>
            {t('carSharing.carChangesMessage')?.replace('{changerName}', changerName).replace('{carInfo}', carInfo) || 
              `${changerName} has changed details for ${carInfo}.`}
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4 space-y-3 max-h-64 overflow-y-auto">
          {changeEntries.map(([field, change]) => {
            const displayName = fieldDisplayNames[field] || field;
            const oldValue = change.old === null || change.old === '' ? '(empty)' : String(change.old);
            const newValue = change.new === null || change.new === '' ? '(empty)' : String(change.new);
            
            return (
              <div key={field} className="border-b border-gray-200 pb-2 last:border-b-0">
                <p className="text-sm font-medium text-gray-900" style={{ fontFamily: 'Lato' }}>
                  {displayName}
                </p>
                <div className="text-xs text-gray-600 mt-1" style={{ fontFamily: 'Lato' }}>
                  <span className="line-through text-red-600">{oldValue}</span>
                  <span className="mx-2">→</span>
                  <span className="text-green-600">{newValue}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button
            onClick={() => handleResponse('revert')}
            disabled={processing}
            variant="destructive"
            className="bg-red-600 text-white hover:bg-red-700"
            style={{ fontFamily: 'Lato' }}
          >
            {processing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('actions.processing') || 'Processing...'}
              </>
            ) : (
              t('carSharing.revert') || 'Revert'
            )}
          </Button>
          <Button
            onClick={() => handleResponse('accept')}
            disabled={processing}
            className="bg-[#E7FE52] text-black hover:opacity-90"
            style={{ fontFamily: 'Lato' }}
          >
            {processing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('actions.processing') || 'Processing...'}
              </>
            ) : (
              t('carSharing.accept') || 'Accept'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CarChangeNotification;

